import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, Stack, Chip,
  Skeleton, Alert, Button, IconButton,
  Menu, MenuItem, ListItemText, Divider, Tooltip,
} from '@mui/material';
import axios from 'axios';
import PeriodPicker, { defaultPeriodValue } from './PeriodPicker';
import RefreshIcon from '@mui/icons-material/Refresh';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { DataGridPro } from '@mui/x-data-grid-pro';

import KpiCard, {
  fmtAUDCompact, fmtMWhCompact, fmtPct, fmtAUDPerMWh,
} from './KpiCard';
import { useThemeColors } from '../theme/ThemeModeContext';
import PortfolioMap from './PortfolioMap';
import StatusPill from './StatusPill';
import { cachedGet } from '../utils/cachedFetch';
import { periodApiParams } from '../utils/periodParams';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const FUEL_COLOR = {
  Solar:   '#f59e0b',
  Wind:    '#3b82f6',
  Battery: '#8b5cf6',
  Other:   '#6b7280',
};

// Curtailment causes → colour palette + full label
const CAUSE_COLOR = {
  'Network':       '#3b82f6',
  'Thermal limit': '#f59e0b',
  'Neg. price':    '#ef4444',
  'Frequency':     '#8b5cf6',
  'Other':         '#6b7280',
};
const CAUSE_LABEL = {
  'Network':       'AEMO directed (network)',
  'Thermal limit': 'Local thermal limit',
  'Neg. price':    'Negative price (econ.)',
  'Frequency':     'Frequency response',
  'Other':         'Other / unattributed',
};

const PERIOD_OPTIONS = [
  { value: 'mtd', label: 'MTD' },
  { value: '7d',  label: '7D' },
  { value: '30d', label: '30D' },
  { value: '90d', label: '90D' },
  { value: 'ytd', label: 'YTD' },
];

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};


// ── AssetPicker ────────────────────────────────────────────────────────────
//
// Compact dropdown for switching the active asset on the Portfolio Overview.
// Uses a Button trigger + popover Menu (instead of a bare <Select>) so it
// matches the Aurora outline-button aesthetic the rest of the chrome uses.
//
// Props:
//   value       : 'all' | <duid>
//   onChange    : (newValue) => void
//   assets      : list of {asset_id, name, region, fuel}
//   colors      : theme palette
//   allowAll    : whether to include an "All assets" option (default true)
//   placeholder : text shown when value is falsy (e.g. while initial dispatch
//                 fetch is in flight)
//   minWidth    : optional override for trigger width

function AssetPicker({
  value, onChange, assets, colors,
  allowAll = true, placeholder = 'Select asset', minWidth = 220,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const selected = value && value !== 'all'
    ? (assets || []).find((a) => a.asset_id === value)
    : null;

  const triggerLabel = value === 'all'
    ? 'All assets'
    : (selected?.name || placeholder);

  const triggerSecondary = value === 'all'
    ? `${(assets || []).length} ${(assets || []).length === 1 ? 'asset' : 'assets'}`
    : (selected ? `${selected.region} · ${selected.fuel}` : '');

  const handlePick = (v) => {
    setAnchorEl(null);
    onChange(v);
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={<ViewModuleOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
        endIcon={<ExpandMoreIcon sx={{
          fontSize: 18,
          transition: 'transform 160ms ease',
          transform: open ? 'rotate(180deg)' : 'none',
        }} />}
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          color: 'text.primary',
          borderColor: colors.hairline2,
          bgcolor: 'transparent',
          py: 0.55, px: 1.25,
          minWidth,
          justifyContent: 'space-between',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: colors.hover,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, flex: 1 }}>
          <Typography sx={{
            fontSize: '0.82rem', fontWeight: 600, lineHeight: 1.2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}>
            {triggerLabel}
          </Typography>
          {triggerSecondary && (
            <Typography sx={{
              fontSize: '0.66rem', color: 'text.disabled',
              lineHeight: 1.1, letterSpacing: '0.02em',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}>
              {triggerSecondary}
            </Typography>
          )}
        </Box>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.5, minWidth: Math.max(minWidth, 260),
            border: '1px solid', borderColor: colors.hairline2,
            borderRadius: 1.5,
            bgcolor: colors.surface,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          },
        }}
        MenuListProps={{ sx: { py: 0.5 } }}
      >
        {allowAll && (
          <MenuItem
            selected={value === 'all'}
            onClick={() => handlePick('all')}
            sx={{
              py: 0.9, px: 1.5,
              '&.Mui-selected': { bgcolor: colors.hover },
              '&.Mui-selected:hover': { bgcolor: colors.hover },
            }}
          >
            <ListItemText
              primary="All assets"
              secondary={`Portfolio aggregate · ${(assets || []).length} ${(assets || []).length === 1 ? 'asset' : 'assets'}`}
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: '0.7rem', color: 'text.disabled' }}
            />
            {value === 'all' && <CheckIcon sx={{ fontSize: 16, color: 'primary.main', ml: 1 }} />}
          </MenuItem>
        )}
        {allowAll && <Divider sx={{ borderColor: colors.hairline, my: 0.25 }} />}
        {(assets || []).map((a) => (
          <MenuItem
            key={a.asset_id}
            selected={a.asset_id === value}
            onClick={() => handlePick(a.asset_id)}
            sx={{
              py: 0.9, px: 1.5,
              '&.Mui-selected': { bgcolor: colors.hover },
              '&.Mui-selected:hover': { bgcolor: colors.hover },
            }}
          >
            <FiberManualRecordIcon sx={{ fontSize: 9, mr: 1.25, color: FUEL_COLOR[a.fuel] || FUEL_COLOR.Other }} />
            <ListItemText
              primary={a.name}
              secondary={`${a.asset_id} · ${a.region} · ${a.fuel}`}
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: '0.7rem', color: 'text.disabled' }}
            />
            {a.asset_id === value && <CheckIcon sx={{ fontSize: 16, color: 'primary.main', ml: 1 }} />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}


// ── Active Alerts panel ────────────────────────────────────────────────────
//
// Shows the top-N most recent alerts surfaced by /api/portfolios/alerts. Live
// data — the alerts module computes these in real time off DISPATCHLOAD +
// capture-price snapshots. If we get an empty list back, the empty-state
// reassures the user with a green "All clear" message.

const SEV_META = {
  critical: { color: '#ef4444', icon: ErrorOutlineIcon,    label: 'CRITICAL' },
  warning:  { color: '#f59e0b', icon: WarningAmberIcon,    label: 'WARNING'  },
  info:     { color: '#3b82f6', icon: InfoOutlinedIcon,    label: 'INFO'     },
};

function fmtRelTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const sec = Math.max(0, Math.round(ms / 1000));
  if (sec < 60)         return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60)         return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24)          return `${hr}h ago`;
  const d = Math.round(hr / 24);
  return `${d}d ago`;
}

// Aurora "Project deadlines"-style card: left column has a donut chart that
// aggregates the alerts by severity with the total in the centre, right
// column is a scrollable list of every active alert. The two columns share
// the same card surface and stack into one column on narrow screens.
function ActiveAlertsCard({ alerts, loading, colors }) {
  const items = alerts?.alerts || [];
  const totalCount = items.length;

  // Severity filter — click a donut segment or a legend chip to scope the
  // list below. `null` = no filter (show all). Clicking the active filter
  // again toggles it off.
  const [filterSev, setFilterSev] = useState(null);

  // Aggregate counts per severity for the donut. We always render the three
  // standard buckets even at zero, so the legend layout stays stable.
  const sevCounts = useMemo(() => {
    const out = { critical: 0, warning: 0, info: 0 };
    for (const a of items) {
      const k = (a.severity || 'info').toLowerCase();
      out[k] = (out[k] ?? 0) + 1;
    }
    return out;
  }, [items]);

  const donutData = [
    { id: 'critical', label: 'Critical', value: sevCounts.critical, color: SEV_META.critical.color },
    { id: 'warning',  label: 'Warning',  value: sevCounts.warning,  color: SEV_META.warning.color  },
    { id: 'info',     label: 'Info',     value: sevCounts.info,     color: SEV_META.info.color     },
  ].filter(d => d.value > 0);

  // Apply the severity filter to the list. We also surface the filtered
  // count in the centre of the donut so users can see the impact of the cut.
  const filteredItems = useMemo(() => (
    filterSev
      ? items.filter(a => (a.severity || 'info').toLowerCase() === filterSev)
      : items
  ), [items, filterSev]);
  const displayCount = filterSev ? filteredItems.length : totalCount;

  const toggleSev = (sev) => setFilterSev((prev) => (prev === sev ? null : sev));

  return (
    <Card sx={{ p: 0, overflow: 'hidden' }}>
      {/* Card header — title pill + view-all link, same chrome as the rest of the page */}
      <Stack direction="row" alignItems="baseline" justifyContent="space-between"
             sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1.25} alignItems="baseline">
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            Active alerts
          </Typography>
          {totalCount > 0 && (
            <StatusPill tone={sevCounts.critical > 0 ? 'red' : 'amber'} size="sm">
              {totalCount}
            </StatusPill>
          )}
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ p: 2 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={42}
                      sx={{ bgcolor: colors.skeleton, borderRadius: 1, mb: 1 }} />
          ))}
        </Box>
      ) : totalCount === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 32, color: 'success.main', mb: 0.5 }} />
          <Typography sx={{ color: 'text.secondary' }}>
            All clear — no active alerts in the last 7 days.
          </Typography>
        </Box>
      ) : (
        <Box>
          {/* Top section — donut + horizontal severity legend, stacked above the list */}
          <Box sx={{
            px: 2, pt: 2.5, pb: 2,
            borderBottom: '1px solid', borderColor: 'divider',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', gap: 1.5,
          }}>
            <Box sx={{ position: 'relative', width: 150, height: 150 }}>
              <PieChart
                width={150}
                height={150}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                hideLegend
                // Click a slice to filter the list below to that severity.
                // Click the same slice again to clear the filter.
                onItemClick={(_event, item) => {
                  const picked = donutData[item.dataIndex]?.id;
                  if (picked) toggleSev(picked);
                }}
                series={[{
                  data: donutData.map(d => ({
                    ...d,
                    // Visually dim non-selected segments when a filter is active.
                    color: !filterSev || filterSev === d.id
                      ? d.color
                      : `${d.color}55`,  // 33% alpha hex suffix
                  })),
                  innerRadius: 50,
                  outerRadius: 70,
                  paddingAngle: 1.5,
                  cornerRadius: 2,
                  highlightScope: { faded: 'global', highlighted: 'item' },
                  faded: { innerRadius: 50, additionalRadius: -2, color: 'gray' },
                }]}
                sx={{ cursor: 'pointer' }}
              />
              {/* Centre overlay — count + label. Shows filtered count when
                  a severity is selected, plus the colour cue from the filter. */}
              <Box sx={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'none',
              }}>
                <Typography sx={{
                  fontSize: '1.85rem', fontWeight: 700, lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                  color: filterSev ? SEV_META[filterSev].color : 'text.primary',
                  transition: 'color 160ms ease',
                }}>
                  {displayCount}
                </Typography>
                <Typography sx={{
                  fontSize: '0.65rem', color: 'text.disabled',
                  letterSpacing: '0.08em', textTransform: 'uppercase', mt: 0.4,
                }}>
                  {filterSev ? filterSev : 'Active'}
                </Typography>
              </Box>
            </Box>

            {/* Horizontal legend — click any chip to toggle that severity
                filter. The active chip gets a tinted background; clicking it
                again clears the filter and the list re-expands. */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap"
                   justifyContent="center">
              {[
                { key: 'critical', label: 'Critical' },
                { key: 'warning',  label: 'Warning'  },
                { key: 'info',     label: 'Info'     },
              ].map((row) => {
                const isActive = filterSev === row.key;
                const isDimmed = filterSev && !isActive;
                const sevColor = SEV_META[row.key].color;
                return (
                  <Button
                    key={row.key}
                    onClick={() => toggleSev(row.key)}
                    disabled={sevCounts[row.key] === 0}
                    size="small"
                    sx={{
                      textTransform: 'none',
                      px: 1, py: 0.4,
                      minWidth: 0,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: isActive ? sevColor : colors.hairline2,
                      bgcolor: isActive ? `${sevColor}22` : 'transparent',
                      opacity: isDimmed ? 0.5 : 1,
                      transition: 'all 160ms ease',
                      '&:hover': {
                        bgcolor: isActive ? `${sevColor}33` : colors.hover,
                        borderColor: sevColor,
                      },
                      '&.Mui-disabled': { opacity: 0.35 },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Box sx={{
                        width: 9, height: 9, borderRadius: '50%',
                        bgcolor: sevColor,
                        opacity: sevCounts[row.key] > 0 ? 1 : 0.4,
                      }} />
                      <Typography sx={{
                        fontSize: '0.92rem', fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        color: sevCounts[row.key] > 0 ? 'text.primary' : 'text.disabled',
                      }}>
                        {sevCounts[row.key]}
                      </Typography>
                      <Typography sx={{
                        fontSize: '0.74rem',
                        color: sevCounts[row.key] > 0 ? 'text.secondary' : 'text.disabled',
                      }}>
                        {row.label}
                      </Typography>
                    </Stack>
                  </Button>
                );
              })}
              {filterSev && (
                <Button
                  size="small"
                  onClick={() => setFilterSev(null)}
                  sx={{
                    textTransform: 'none', fontSize: '0.72rem', minWidth: 0,
                    px: 0.75, color: 'text.disabled',
                    '&:hover': { color: 'text.primary', bgcolor: 'transparent' },
                  }}
                >
                  Clear filter
                </Button>
              )}
            </Stack>
          </Box>

          {/* Bottom section — alerts list (filtered by the donut/legend),
              scrollable when long. */}
          <Box sx={{
            maxHeight: 320,
            overflowY: 'auto',
            '&::-webkit-scrollbar':       { width: 6 },
            '&::-webkit-scrollbar-thumb': { bgcolor: colors.hairline2, borderRadius: 3 },
          }}>
            {filteredItems.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <Typography sx={{ color: 'text.disabled', fontSize: '0.85rem' }}>
                  No {filterSev || ''} alerts in this window.
                </Typography>
              </Box>
            ) : filteredItems.map((a, i) => {
              const meta = SEV_META[a.severity] || SEV_META.info;
              const Icon = meta.icon;
              return (
                <Box key={a.id || i} sx={{
                  display: 'grid',
                  gridTemplateColumns: '20px 1fr auto auto',
                  gap: 1.25, alignItems: 'center',
                  px: 2, py: 1.2,
                  borderBottom: '1px solid', borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover':      { bgcolor: colors.hover },
                  transition: 'background-color 120ms ease',
                }}>
                  <Icon sx={{ fontSize: 16, color: meta.color }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{
                      fontWeight: 600, fontSize: '0.88rem', color: 'text.primary',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {a.title || a.message || 'Alert'}
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center"
                           sx={{ color: 'text.disabled', fontSize: '0.72rem', mt: 0.15 }}>
                      {a.asset_name && (
                        <Box component="span" sx={{ color: 'text.secondary' }}>
                          {a.asset_name}
                        </Box>
                      )}
                      {a.category && (
                        <>
                          <Box component="span">·</Box>
                          <Box component="span" sx={{ textTransform: 'capitalize' }}>
                            {a.category}
                          </Box>
                        </>
                      )}
                      {a.opened_at && (
                        <>
                          <Box component="span">·</Box>
                          <Box component="span">{fmtRelTime(a.opened_at)}</Box>
                        </>
                      )}
                    </Stack>
                  </Box>
                  {a.impact_aud != null ? (
                    <StatusPill tone="amber" size="sm">
                      {fmtAUDCompact(Math.abs(a.impact_aud))}
                    </StatusPill>
                  ) : <Box />}
                  <StatusPill tone={
                    a.severity === 'critical' ? 'red' :
                    a.severity === 'warning'  ? 'amber' : 'blue'
                  } size="sm">
                    {meta.label}
                  </StatusPill>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Card>
  );
}


export default function PortfolioOverview() {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  // Mode-aware colour ramp — flips with the theme toggle.
  const colors = useThemeColors();
  // 30D default — gives users ~30 days of data on first load instead of just
  // the few days since month-start (which can look empty early in a month).
  // PeriodPicker emits { code, start, end, frequency, label } — `code` is the
  // preset name when one of the chips is active, or 'custom' for a custom range.
  const [period, setPeriod] = useState(() => defaultPeriodValue('30d'));
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [alerts, setAlerts] = useState(null);   // {alerts:[], counts:{}, period:{}}
  // (Asset benchmark summary + Edit dialog state lived here — both moved
  // to the dedicated Benchmarks page.)
  // Asset scope: 'all' = portfolio aggregate; otherwise a specific DUID.
  // Backend honours ?asset_id=...; when set, every chart on the page reflects
  // that single asset and the single-asset detail block fires.
  const [assetId, setAssetId] = useState('all');
  async function load(periodValue, scope) {
    setLoading(true); setError(null);
    try {
      // Explicit start/end for every preset — the picker is the single
      // source of truth for the window (see utils/periodParams.js).
      const params = periodApiParams(periodValue, { withFrequency: false });
      if (scope && scope !== 'all') params.asset_id = scope;
      // cachedGet — same (URL + params) within 5min returns instantly from
      // the in-tab cache. Stops period-flipping from re-firing /overview.
      const r = await cachedGet(
        `${API_BASE_URL}/api/portfolios/${portfolioId}/overview`,
        { params },
      );
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(period, assetId); /* eslint-disable-next-line */ }, [portfolioId, period, assetId]);

  // Active alerts feed — runs in parallel with the main overview fetch.
  // We now scope to the current portfolio (the endpoint defaults to
  // tenant-wide, but passing `portfolio_id` narrows it). This is what
  // makes the alerts panel actually reflect "what's wrong with the
  // assets I'm looking at right now" vs the user's whole holding.
  useEffect(() => {
    if (!portfolioId) return;
    let cancelled = false;
    cachedGet(`${API_BASE_URL}/api/portfolios/alerts`, {
      params: { period: '7d', portfolio_id: portfolioId },
    })
      .then((r) => { if (!cancelled) setAlerts(r.data); })
      .catch(() => { if (!cancelled) setAlerts({ alerts: [], counts: {} }); });
    return () => { cancelled = true; };
  }, [portfolioId]);

  // (Benchmark summary fetch + Edit-benchmarks handlers moved to the
  // dedicated Benchmarks page.)

  // (Dispatch fetch has moved to Commercial Performance › Overview.)

  // revenue_series is {bucket_minutes, mock_contract, series} — the chart
  // adapts its x-axis grain and renders three overlays (actual / contracted /
  // spot-only) so users can see the hedging impact.
  const revenuePayload = data?.revenue_series || { bucket_minutes: 1440, series: [] };
  const series = revenuePayload.series || [];
  const revenueBucketMin = revenuePayload.bucket_minutes || 1440;
  // Backend now ships actual PPA terms in `contract` (was `mock_contract`).
  // Keep both keys readable while old cached responses linger.
  const contract = revenuePayload.contract || revenuePayload.mock_contract || null;
  const contractIsReal = !!(contract && contract.contracted_share > 0);
  const captureFuels = data?.capture_by_fuel || [];

  // Build chart data — three revenue overlays plus generation, with a
  // YoY (same-period-last-year) overlay shifted onto the current x-axis.
  // We align by ordinal position (1st bucket → 1st bucket, etc.) rather
  // than calendar date — sidesteps leap-year / month-length mismatches
  // and works for any bucket size the backend chose.
  const priorYearPayload = data?.revenue_series_prior_year || { series: [] };
  const priorYearSeries = priorYearPayload.series || [];
  const chart = useMemo(() => {
    if (!series.length) return null;
    const xs         = series.map(p => new Date(p.date));
    const actual     = series.map(p => p.actual_aud ?? p.revenue_aud);
    const contracted = series.map(p => p.contracted_aud ?? 0);
    const spot       = series.map(p => p.spot_aud ?? p.revenue_aud);
    const gen        = series.map(p => p.generation_mwh);
    // YoY series — same length window one year ago. Map each current
    // bucket index → corresponding prior-year bucket so the eye can read
    // "above or below same week last year" at a glance. If prior-year
    // data is missing (asset commissioned within last 12 months), the
    // series is just nulls and the chart line never renders.
    const yoy = xs.map((_, i) => {
      const p = priorYearSeries[i];
      return p ? (p.actual_aud ?? p.revenue_aud ?? null) : null;
    });
    return { xs, actual, contracted, spot, gen, yoy, rev: actual };
  }, [series, priorYearSeries]);

  const captureChips = captureFuels.length ? captureFuels.map(c => ({
    fuel: c.fuel,
    capture: c.capture_aud_per_mwh,
    mwh: c.generation_mwh,
  })) : null;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      {/* Header strip — matches the chrome on Commercial Performance and
          Facility & Covenants: 1.5rem bold title + tight letter-spacing,
          consistent outer padding so the three sections feel like one app. */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography sx={{
            fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.025em',
            color: 'text.primary',
          }}>
            {data?.portfolio?.name || (loading ? <Skeleton width={220} /> : 'Portfolio')}
          </Typography>
          {data?.portfolio && (
            <>
              <Chip size="small" variant="outlined"
                label={`${data.portfolio.asset_count} ${data.portfolio.asset_count === 1 ? 'asset' : 'assets'}`} />
              {data.portfolio.total_capacity_mw > 0 && (
                <Chip size="small" variant="outlined"
                  label={`${Math.round(data.portfolio.total_capacity_mw)} MW`} />
              )}
            </>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          {/* Asset scope selector — only shown for multi-asset portfolios so
              the user can pivot every chart on the page to a single DUID. */}
          {data?.portfolio && data.portfolio.asset_count > 1 && (
            <AssetPicker
              value={assetId}
              onChange={setAssetId}
              assets={data?.assets || []}
              colors={colors}
              allowAll
              minWidth={220}
            />
          )}
          <PeriodPicker value={period} onChange={setPeriod} size="small" />
          <IconButton size="small" onClick={() => load(period, assetId)} sx={{ color: 'text.secondary' }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {/* Sub-header: explicit date range + live status — sourced from the picker
          state (not the last response) so it tracks the user's selection
          immediately, even while the new fetch is in flight. */}
      {period && (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5, ml: 0.25 }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            color: 'text.secondary', fontSize: '0.8rem',
          }}>
            <FiberManualRecordIcon sx={{ fontSize: 8, color: 'primary.main' }} />
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {period.label}
            </Box>
            <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {fmtDate(period.start)}<Box component="span" sx={{ mx: 0.5, color: 'text.disabled' }}>→</Box>{fmtDate(period.end)}
            </Box>
          </Box>
        </Stack>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* KPI row — 5 portfolio-level tiles + one tile per fuel for the
          per-fuel weighted capture breakdown. All in the same auto-flow grid
          so the per-fuel tiles read as a continuation of the strip, not a
          separate section. The dedicated Active Alerts panel below carries
          the live count + list, so we don't repeat it as a KPI tile here.
          Column count adapts to total tile count. */}
      {/* Top row — left column (60%) stacks the KPI grid over the Assets in
          portfolio roster; right column (40%) is the asset map, which spans
          the full height of the row alongside both. Puts the portfolio
          summary, the asset roster AND the geography in one above-the-fold
          view. */}
      <Box sx={{
        display: 'grid', gap: 1.5, mb: 2.5,
        gridTemplateColumns: { xs: '1fr', lg: '60fr 40fr' },
        alignItems: 'stretch',
      }}>
      {/* Left column — KPI tiles on top, asset roster beneath. `auto 1fr`
          lets the tile grid take its natural height and hands the remaining
          row height to the roster, so the column's bottom edge lines up with
          the map alongside it. */}
      <Box sx={{
        display: 'grid', gap: 1.5, minWidth: 0,
        gridTemplateRows: { lg: 'auto 1fr' },
      }}>
      {(() => {
        const fuelTiles = captureChips || [];
        const totalTiles = 5 + fuelTiles.length;
        return (
          <Box sx={{
            display: 'grid', gap: 1.5,
            // 3-column grid; rows wrap based on tile count (5 fixed + N fuel).
            // gridAutoRows: 1fr keeps every card the same height as its row.
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gridAutoRows: '1fr',
          }}>
            <KpiCard
              label={`Revenue · ${period?.label?.split(' ')[0] || ''}`}
              loading={loading}
              value={fmtAUDCompact(data?.kpis?.revenue_aud)}
              accent={colors.ACCENT}
            />
            <KpiCard
              label="Generation"
              loading={loading}
              value={fmtMWhCompact(data?.kpis?.generation_mwh)}
              accent="#3b82f6"
            />
            <KpiCard
              label="Weighted capture"
              loading={loading}
              value={fmtAUDPerMWh(data?.kpis?.weighted_capture_aud_per_mwh)}
              unit="/MWh"
              accent="#f59e0b"
              tooltip="Generation-weighted average dispatch price across all assets in this period"
            />
            <KpiCard
              label="Availability"
              loading={loading}
              value={fmtPct(data?.kpis?.availability_pct, 1)}
              accent="#06b6d4"
              tooltip="Average TOTALCLEARED / REGISTEREDCAPACITY across portfolio assets"
            />
            {/* Min DSCR · fwd 12M tile removed — re-add when the covenant
                module (Cluster 4) is live. Until then the placeholder reads
                as a permanent "—" which dilutes the KPI strip. */}
            {fuelTiles.map((c) => (
              <KpiCard
                key={c.fuel}
                label={`Capture · ${c.fuel}`}
                loading={loading}
                value={fmtAUDPerMWh(c.capture)}
                unit={`/MWh · ${fmtMWhCompact(c.mwh)}`}
                accent={FUEL_COLOR[c.fuel] || FUEL_COLOR.Other}
                tooltip={`Weighted capture for ${c.fuel.toLowerCase()} assets · ${period?.label || ''}`}
              />
            ))}
            {/* While loading from a cold start, the fuel breakdown isn't in
                data yet, so prior captureChips is null. Render 3 placeholder
                tiles so the strip doesn't visibly shrink mid-load. */}
            {loading && fuelTiles.length === 0 && [0, 1, 2].map((i) => (
              <KpiCard key={`fuel-ph-${i}`} label="Capture" loading />
            ))}
          </Box>
        );
      })()}

        {/* Assets in portfolio — replaces the stiff DataGrid with a richer
            list of asset "cards": fuel-coloured stripe on the left edge,
            asset name + DUID + region·fuel meta, utilisation bar showing
            current output vs nameplate capacity, and a tinted availability
            pill on the right. Sorted by latest output desc so the workhorses
            sit at the top; scrollable when the portfolio is large.

            Reads as a portfolio "watchlist" rather than a spreadsheet — the
            same information density but at a glance, with the proportions
            (utilisation bars, status pills) doing more work than rows. */}
        {/* No outer accent stripe — each asset row carries its own fuel-
            coloured stripe (Solar=amber, Wind=blue, Battery=purple), so a
            single green stripe on the wrapper would compete with those. */}
        <Card sx={{
          p: 0, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center"
                 sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={1.25} alignItems="baseline">
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                Assets in portfolio
              </Typography>
              {data?.portfolio?.total_capacity_mw > 0 && (
                <Typography variant="caption" sx={{
                  color: 'text.disabled', fontVariantNumeric: 'tabular-nums',
                }}>
                  · {Math.round(data.portfolio.total_capacity_mw)} MW total
                </Typography>
              )}
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {data?.assets?.length || 0} {data?.assets?.length === 1 ? 'asset' : 'assets'}
            </Typography>
          </Stack>

          {/* Asset list — scrollable to handle big portfolios. We cap the
              max height to roughly match the KPI grid alongside; the panel
              flex-grows so it always stretches to the row's full height. */}
          <Box sx={{
            flexGrow: 1,
            maxHeight: 480,
            overflowY: 'auto',
            '&::-webkit-scrollbar':       { width: 6 },
            '&::-webkit-scrollbar-thumb': { bgcolor: colors.hairline2, borderRadius: 3 },
          }}>
            {loading ? (
              <Stack spacing={1.25} sx={{ p: 2 }}>
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={64}
                            sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
                ))}
              </Stack>
            ) : !data?.assets?.length ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.disabled">No assets in this portfolio.</Typography>
              </Box>
            ) : (() => {
              // Server already returns assets sorted by avg_mw desc — preserve.
              return data.assets.map((a, idx) => {
                const fuelColor = FUEL_COLOR[a.fuel] || FUEL_COLOR.Other;
                const cap = a.capacity_mw || 0;
                // Show AVERAGE MW over the queried period (MTD / 30d / etc.)
                // not the latest 5-min reading — the latest reading is 0
                // when the user opens the dashboard outside daylight
                // hours, which reads as broken for solar farms. Falls
                // back to latest_mw on older backend responses.
                const mw  = (a.avg_mw != null ? a.avg_mw : a.latest_mw) || 0;
                const utilPct = cap > 0 ? (Math.abs(mw) / cap) * 100 : 0;
                const avail = a.availability_pct;
                // Availability ramp — green / amber / red / grey based on
                // typical NEM thresholds. Solar/Wind stay neutral: AEMO
                // AVAILABILITY follows the resource (0 overnight), so the
                // averaged % sits at ~10–25% and the red threshold would
                // flag every healthy intermittent asset. Plant-level
                // availability needs SCADA — until then the number is
                // informational only for those fuels.
                const isIntermittentFuel = a.fuel === 'Solar' || a.fuel === 'Wind';
                const availTone =
                  avail == null || isIntermittentFuel
                                   ? { fill: colors.textMute, bg: `${colors.textMute}1f` } :
                  avail >= 90      ? { fill: '#22c55e',       bg: 'rgba(34,197,94,0.12)' } :
                  avail >= 70      ? { fill: '#f59e0b',       bg: 'rgba(245,158,11,0.14)' } :
                                     { fill: '#ef4444',       bg: 'rgba(239,68,68,0.14)' };
                return (
                  <Box
                    key={a.asset_id}
                    sx={{
                      position: 'relative',
                      display: 'flex', alignItems: 'stretch',
                      px: 2, py: 1.6,
                      borderBottom: idx < data.assets.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      transition: 'background-color 120ms ease',
                      '&:hover': { bgcolor: colors.hover },
                    }}
                  >
                    {/* Fuel-coloured stripe on the left edge */}
                    <Box sx={{
                      position: 'absolute', left: 0, top: 8, bottom: 8, width: 3,
                      bgcolor: fuelColor, borderRadius: '0 2px 2px 0',
                    }} />

                    <Box sx={{ flexGrow: 1, minWidth: 0, pl: 0.75 }}>
                      {/* Top line: name + status pill on the right */}
                      <Stack direction="row" alignItems="center" justifyContent="space-between"
                             spacing={1.25} sx={{ mb: 0.4 }}>
                        <Typography sx={{
                          fontWeight: 600, fontSize: '0.9rem', color: 'text.primary',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          minWidth: 0,
                        }}>
                          {a.name || a.asset_id}
                        </Typography>
                        <Box sx={{
                          flexShrink: 0,
                          display: 'inline-flex', alignItems: 'center', gap: 0.4,
                          px: 0.85, py: 0.2, borderRadius: '6px',
                          bgcolor: availTone.bg, color: availTone.fill,
                          fontSize: '0.7rem', fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums', lineHeight: 1,
                        }}>
                          <FiberManualRecordIcon sx={{ fontSize: 8 }} />
                          {avail != null ? `${avail.toFixed(0)}%` : '—'}
                        </Box>
                      </Stack>

                      {/* Sub-line: meta — DUID · region · fuel */}
                      <Stack direction="row" alignItems="center" spacing={0.85}
                             sx={{ fontSize: '0.72rem', color: 'text.disabled',
                                   mb: 0.85, flexWrap: 'wrap' }}>
                        <Box component="span" sx={{
                          fontFamily: 'monospace', fontSize: '0.7rem',
                          color: 'text.secondary',
                        }}>
                          {a.asset_id}
                        </Box>
                        <Box component="span">·</Box>
                        <Box component="span">{a.region || '—'}</Box>
                        <Box component="span">·</Box>
                        <Box component="span" sx={{ color: fuelColor, fontWeight: 600 }}>
                          {a.fuel || 'Other'}
                        </Box>
                      </Stack>

                      {/* Utilisation bar — current output ÷ nameplate */}
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Box sx={{
                          flexGrow: 1, height: 6, borderRadius: 2,
                          bgcolor: colors.track, overflow: 'hidden',
                        }}>
                          <Box sx={{
                            height: '100%',
                            width: `${Math.min(utilPct, 100)}%`,
                            bgcolor: fuelColor, opacity: 0.9,
                            transition: 'width 240ms ease',
                          }} />
                        </Box>
                        <Tooltip title={a.avg_mw != null
                            ? `Average MW over ${period?.label || 'the selected period'}`
                            : 'Most recent 5-minute dispatch'}>
                          <Typography sx={{
                            fontSize: '0.78rem', fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            color: 'text.primary',
                            whiteSpace: 'nowrap', minWidth: 110, textAlign: 'right',
                          }}>
                            {mw.toFixed(1)}<Box component="span" sx={{
                              color: 'text.disabled', fontWeight: 500, ml: 0.4,
                            }}>
                              / {Math.round(cap)} MW
                              {a.avg_mw != null && (
                                <Box component="span" sx={{ ml: 0.5, fontSize: '0.66rem' }}>
                                  avg
                                </Box>
                              )}
                            </Box>
                          </Typography>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </Box>
                );
              });
            })()}
          </Box>
        </Card>
      </Box>
      {/* ── end left column ── */}

      {/* Asset locations — the row's right-hand 40%, level with the KPI tiles
          and the roster so "what do I own and where is it" reads as one block.
          Zoom tracks the asset selector above: one asset selected frames that
          site on satellite imagery, "All assets" fits the whole portfolio.
          Clicking Focus in a pin's popup drives the same `assetId` state as
          the picker, so the two controls stay in sync. */}
      <PortfolioMap
        assets={data?.assets || []}
        selectedAssetId={assetId}
        onSelectAsset={setAssetId}
        loading={loading}
        colors={colors}
        period={period}
        fillHeight
      />
      </Box>

      {/* Dispatch & spot price chart has moved to Commercial Performance ›
          Overview (it's an asset-level analytical view, not a portfolio
          summary). The Portfolio Overview page now goes straight from the
          KPI strip into the Portfolio revenue chart. */}

      {/* Layer 3: Portfolio revenue (period-driven, full width)
          Three overlays: actual (blended), contracted (CFD fixed slice), spot-only. */}
      <Card sx={{ p: 2.5, mb: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1.25} alignItems="baseline">
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Portfolio revenue
            </Typography>
            {contractIsReal && (
              <StatusPill tone="green" size="sm">
                CFD · {Math.round(contract.contracted_share * 100)}% @ ${Number(contract.strike_aud).toFixed(2)}/MWh
              </StatusPill>
            )}
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {(() => {
              if (revenueBucketMin === 1440)  return 'Daily · Australia/Sydney';
              if (revenueBucketMin === 10080) return 'Weekly · Australia/Sydney';
              return 'Monthly · Australia/Sydney';
            })()}
          </Typography>
        </Stack>
        {loading ? (
          <Skeleton variant="rectangular" height={260} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
        ) : !chart ? (
          <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.disabled">No revenue data for the selected window.</Typography>
          </Box>
        ) : (
          <LineChart
            height={260}
            xAxis={[{
              data: chart.xs,
              scaleType: 'time',
              valueFormatter: (v, ctx) => {
                const d = v instanceof Date ? v : new Date(v);
                const tip = ctx?.location === 'tooltip';
                if (revenueBucketMin === 1440) {
                  return tip
                    ? d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
                    : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
                }
                if (revenueBucketMin === 10080) {
                  return tip
                    ? `Week of ${d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`
                    : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
                }
                // monthly
                return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
              },
              tickLabelStyle: { fill: colors.textDim, fontSize: 11 },
            }]}
            yAxis={[{
              valueFormatter: (v) => fmtAUDCompact(v),
              tickLabelStyle: { fill: colors.textDim, fontSize: 11 },
            }]}
            series={[
              // Actual blended P&L — primary line with area fill underneath.
              { data: chart.actual,
                label: 'Actual (blended)',
                color: colors.ACCENT,
                area: true,
                showMark: false,
                valueFormatter: (v) => v == null ? '—' : fmtAUDCompact(v) },
              // Contracted slice — what the CFD locks in (fixed strike × hedged MWh).
              { data: chart.contracted,
                label: 'Contracted (CFD)',
                color: '#E9B065',
                showMark: false,
                valueFormatter: (v) => v == null ? '—' : fmtAUDCompact(v) },
              // Counterfactual: what we'd have earned with no hedge at all.
              { data: chart.spot,
                label: 'Spot only',
                color: '#f59e0b',
                showMark: false,
                valueFormatter: (v) => v == null ? '—' : fmtAUDCompact(v) },
              // Same period one year ago — dashed grey overlay so the eye
              // can read "above or below last year" without flipping pages.
              ...(chart.yoy?.some(v => v != null) ? [{
                data: chart.yoy,
                label: 'Same period last year',
                color: '#94a3b8',
                curve: 'monotoneX',
                showMark: false,
                valueFormatter: (v) => v == null ? '—' : fmtAUDCompact(v),
              }] : []),
            ]}
            grid={{ horizontal: true }}
            margin={{ left: 56, right: 16, top: 30, bottom: 30 }}
            slotProps={{
              legend: { position: { vertical: 'top', horizontal: 'right' } },
            }}
            sx={{
              '& .MuiChartsAxis-line': { stroke: colors.hairline2 },
              '& .MuiChartsAxis-tick':  { stroke: colors.hairline2 },
              '& .MuiChartsGrid-line':  { stroke: colors.hairline },
              '& .MuiAreaElement-root': { fillOpacity: 0.16 },
            }}
          />
        )}
      </Card>

      {/* Asset-owner triage row — underperformer leaderboard (left 60%) +
          forward calendar (right 40%). This is the "what should I look at"
          surface — answers the top-2 questions an asset manager asks when
          opening the page: "where are problems?" and "what's coming up?". */}
      <Box sx={{
        display: 'grid', gap: 1.5, mb: 2.5,
        gridTemplateColumns: { xs: '1fr', lg: '6fr 4fr' },
        alignItems: 'start',
      }}>
        <UnderperformerLeaderboard
          // Server-computed: bottom 3 + portfolio median CF. Both are emitted
          // on data.kpis so the agent and the UI agree on the numbers.
          ranked={{
            bottom: data?.kpis?.bottom_n_underperformers || [],
            median: data?.kpis?.median_cf_pct ?? null,
            total: (data?.assets || []).filter(a => a.cf_pct != null).length,
          }}
          loading={loading}
          colors={colors}
        />
        <ForwardCalendarCard
          assets={data?.assets || []}
          loading={loading}
          colors={colors}
        />
      </Box>

      {/* Below Portfolio revenue: left column = Revenue mix → Active alerts
          (money-then-risk reading flow), right column = Asset health
          (operational pulse). Both columns sit side-by-side so an asset
          owner can scan revenue + alerts on the left while spot-checking
          asset state on the right in one pass.

          The right column needs `minWidth: 0` on its child to keep grid
          tracks from being pushed wider than their column when the inner
          card has wide content. */}
      <Box sx={{
        display: 'grid', gap: 1.5, mb: 2.5,
        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        alignItems: 'start',
      }}>
        <Box sx={{ display: 'grid', gap: 1.5, minWidth: 0 }}>
          <RevenueMixCard
            // Server-computed: revenue_mix_by_fuel[] + esg{co2_displaced_t,
            // lgcs_eligible, homes_equivalent}. JS used to do this via reduce.
            fuelTotals={data?.revenue_mix_by_fuel || []}
            esg={data?.esg || null}
            loading={loading}
            colors={colors}
          />
          <ActiveAlertsCard
            alerts={alerts}
            loading={alerts == null}
            colors={colors}
          />
        </Box>
        <Box sx={{ display: 'grid', gap: 1.5, minWidth: 0 }}>
        {/* Asset health — list of rows with availability progress bar + capacity-weighted footer bar */}
        <Card sx={{ p: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Asset health
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Availability · sorted by capacity
            </Typography>
          </Stack>
          {loading ? (
            <Stack spacing={1}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} variant="rectangular" height={48}
                          sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
              ))}
            </Stack>
          ) : !data?.assets?.length ? (
            <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.disabled">No assets to show.</Typography>
            </Box>
          ) : (
            <Stack spacing={1.25}>
              {(() => {
                const sorted = [...data.assets].sort((a, b) => (b.capacity_mw || 0) - (a.capacity_mw || 0));
                const maxCap = sorted[0]?.capacity_mw || 1;
                return sorted.map((a) => {
                  const avail = a.availability_pct || 0;
                  // Aurora colour ramp — solid colour for the bar fill, tinted background for the row
                  const isGood   = avail >= 90;
                  const isOk     = avail >= 70;
                  const isAmber  = avail >= 40;
                  const fillColor = isGood ? '#22c55e' : isOk ? '#D98A2B' : isAmber ? '#f59e0b' : '#ef4444';
                  const fuelColor = FUEL_COLOR[a.fuel] || FUEL_COLOR.Other;
                  const capShare = (a.capacity_mw || 0) / maxCap;

                  return (
                    <Box
                      key={a.asset_id}
                      sx={{
                        position: 'relative',
                        px: 1.5, py: 1.1,
                        borderRadius: 1.5,
                        border: '1px solid', borderColor: 'divider',
                        transition: 'border-color 120ms ease, background-color 120ms ease',
                        '&:hover': { borderColor: colors.hairline2, bgcolor: colors.hover },
                      }}
                    >
                      {/* Top row: fuel dot + name | availability % */}
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Box sx={{
                          width: 8, height: 8, borderRadius: '50%',
                          bgcolor: fuelColor, flexShrink: 0,
                        }} />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography sx={{
                            fontWeight: 600, fontSize: '0.92rem', color: 'text.primary',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            lineHeight: 1.2,
                          }}>
                            {a.name}
                          </Typography>
                          <Typography variant="caption" sx={{
                            color: 'text.disabled', fontSize: '0.7rem',
                            letterSpacing: '0.04em',
                          }}>
                            {a.region} · {a.fuel} · {Math.round(a.capacity_mw || 0)} MW
                          </Typography>
                        </Box>
                        <Typography sx={{
                          fontWeight: 600, fontSize: '1rem',
                          fontVariantNumeric: 'tabular-nums',
                          color: fillColor, lineHeight: 1, flexShrink: 0,
                        }}>
                          {avail.toFixed(1)}%
                        </Typography>
                      </Stack>

                      {/* Availability progress bar */}
                      <Box sx={{
                        mt: 1, height: 4, borderRadius: 2,
                        bgcolor: colors.track, overflow: 'hidden',
                      }}>
                        <Box sx={{
                          height: '100%',
                          width: `${Math.min(avail, 100)}%`,
                          bgcolor: fillColor, opacity: 0.85,
                          transition: 'width 240ms ease',
                        }} />
                      </Box>

                      {/* Capacity-weighted footer marker — shows relative size at-a-glance */}
                      <Box sx={{
                        position: 'absolute', left: 0, bottom: 0,
                        width: `${capShare * 100}%`, height: 2,
                        bgcolor: fuelColor, opacity: 0.45,
                        borderBottomLeftRadius: 6,
                        transition: 'width 240ms ease',
                      }} />
                    </Box>
                  );
                });
              })()}
            </Stack>
          )}
        </Card>

        {/* Revenue mix used to live here; moved up next to Active alerts
            in the LEFT column so the money + risk story reads together.
            Right column is now Asset health only. */}
        </Box>
      </Box>

      {/* Asset benchmarks card moved to the dedicated Benchmarks page. */}

      {/* Single-asset detail: just capture-vs-benchmark 12m now.
          (Peer-CF card moved to the dedicated Benchmarks page.)
          Only renders when the portfolio contains exactly one asset (deck slide 11). */}
      {data?.single_asset && (
        <Box sx={{ display: 'grid', gap: 1.5, mb: 2.5 }}>
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: '1fr' }}>
            {/* Capture vs benchmark — period-driven (auto-buckets day/week/month) */}
            <Card sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                  Capture vs benchmark
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  Asset capture · regional avg RRP
                </Typography>
              </Stack>
              {loading ? (
                <Skeleton variant="rectangular" height={200} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
              ) : (() => {
                const captureHist = data.single_asset.capture_history_12m || { bucket_minutes: 43800, series: [] };
                const captureBucketMin = captureHist.bucket_minutes || 43800;
                const hist = (captureHist.series || []).filter(h => h.month);
                if (!hist.length) {
                  return (
                    <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography color="text.disabled">No capture history.</Typography>
                    </Box>
                  );
                }
                const xs = hist.map(h => new Date(h.month));
                const cap = hist.map(h => h.asset_capture);
                const reg = hist.map(h => h.region_avg);
                return (
                  <LineChart
                    height={200}
                    xAxis={[{
                      data: xs,
                      scaleType: 'time',
                      valueFormatter: (v, ctx) => {
                        const d = v instanceof Date ? v : new Date(v);
                        const tip = ctx?.location === 'tooltip';
                        if (captureBucketMin === 1440) {
                          return tip
                            ? d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
                            : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
                        }
                        if (captureBucketMin === 10080) {
                          return tip
                            ? `Week of ${d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`
                            : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
                        }
                        // monthly
                        return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
                      },
                      tickLabelStyle: { fill: colors.textDim, fontSize: 11 },
                    }]}
                    yAxis={[{
                      valueFormatter: (v) => `A$${Math.round(v)}`,
                      tickLabelStyle: { fill: colors.textDim, fontSize: 11 },
                    }]}
                    series={[
                      { data: cap, label: 'Asset capture', color: colors.ACCENT,
                        valueFormatter: (v) => v == null ? '—' : `A$${v.toFixed(2)}/MWh`,
                        showMark: true, connectNulls: true },
                      { data: reg, label: 'Region avg RRP', color: '#E9B065',
                        valueFormatter: (v) => v == null ? '—' : `A$${v.toFixed(2)}/MWh`,
                        showMark: false, connectNulls: true },
                    ]}
                    margin={{ left: 50, right: 16, top: 24, bottom: 30 }}
                    slotProps={{ legend: { position: { vertical: 'top', horizontal: 'right' } } }}
                    sx={{
                      '& .MuiChartsAxis-line': { stroke: colors.hairline2 },
                      '& .MuiChartsAxis-tick':  { stroke: colors.hairline2 },
                      '& .MuiChartsGrid-line':  { stroke: colors.hairline },
                      '& .MuiChartsLegend-label': { fill: colors.textDim, fontSize: '0.75rem' },
                    }}
                  />
                );
              })()}
            </Card>
          </Box>
        </Box>
      )}

      {/* (Active alerts moved up next to Asset health + Curtailment split,
          right below Portfolio revenue. Assets DataGrid moved to the top
          row next to the KPI strip.) */}
      {/* Placeholder remove-me marker so the unused old block below stays
          syntactically valid until the cleanup edit lands. */}
      {false && (
      <Box sx={{
        display: 'grid', gap: 1.5,
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        alignItems: 'start',
      }}>
        <ActiveAlertsCard
          alerts={alerts}
          loading={alerts == null}
          colors={colors}
        />

        <Card sx={{ p: 0, overflow: 'hidden' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            Assets in portfolio
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {data?.assets?.length || 0} {data?.assets?.length === 1 ? 'asset' : 'assets'}
          </Typography>
        </Stack>

        <DataGridPro
          rows={data?.assets || []}
          getRowId={(r) => r.asset_id}
          loading={loading}
          density="compact"
          disableRowSelectionOnClick
          autoHeight
          hideFooterSelectedRowCount
          slotProps={{
            loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' },
          }}
          initialState={{
            sorting: { sortModel: [{ field: 'latest_mw', sort: 'desc' }] },
            pinnedColumns: { left: ['name'] },
          }}
          columns={[
            {
              field: 'name', headerName: 'Asset', flex: 1.6, minWidth: 220,
              renderCell: ({ row }) => (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ height: '100%' }}>
                  <FiberManualRecordIcon sx={{ fontSize: 9, color: FUEL_COLOR[row.fuel] || FUEL_COLOR.Other }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', lineHeight: 1.2 }} noWrap>
                      {row.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', lineHeight: 1.2 }} noWrap>
                      {row.asset_id}
                    </Typography>
                  </Box>
                </Stack>
              ),
            },
            {
              field: 'fuel', headerName: 'Type', width: 110,
              renderCell: ({ value }) => <Chip size="small" variant="outlined" label={value} />,
            },
            { field: 'region', headerName: 'Region', width: 90 },
            {
              field: 'capacity_mw', headerName: 'Capacity', width: 110, type: 'number',
              valueFormatter: (v) => (v ? `${Math.round(v)} MW` : '—'),
              cellClassName: 'tnum',
            },
            {
              field: 'latest_mw', headerName: 'Latest output', width: 130, type: 'number',
              valueFormatter: (v) => (v ? `${v.toFixed(1)} MW` : '—'),
              cellClassName: 'tnum-bold',
            },
            {
              field: 'availability_pct', headerName: 'Avail %', width: 100, type: 'number',
              valueFormatter: (v) => (v ? `${v.toFixed(1)}%` : '—'),
              cellClassName: 'tnum',
            },
          ]}
          sx={{
            border: 0,
            // MUI X DataGrid v8 CSS variables — override every surface to match
            // the Aurora dark card. Keep these in sync with theme.js NEUTRAL ramp.
            '--DataGrid-rowBorderColor':        colors.hairline,
            '--DataGrid-containerBackground':   colors.surface,
            '--DataGrid-pinnedBackground':      colors.surface,
            '--DataGrid-overlayBackground':     colors.surface,
            '--DataGrid-headerBackground':      colors.surface,
            backgroundColor: colors.surface,
            color: 'text.primary',

            // Header row + cells
            '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader': {
              backgroundColor: `${colors.surface} !important`,
              borderBottom: `1px solid ${colors.hairline}`,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.06em',
              textTransform: 'uppercase', color: 'text.secondary',
            },
            '& .MuiDataGrid-columnSeparator': { color: colors.hairline },

            // Row + cell surfaces
            '& .MuiDataGrid-row':         { backgroundColor: 'transparent' },
            '& .MuiDataGrid-row:hover':   { backgroundColor: colors.hover },
            '& .MuiDataGrid-cell':        {
              backgroundColor: 'transparent',
              borderBottom: `1px solid ${colors.hairline}`,
              fontSize: '0.85rem',
              color: 'text.primary',
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none',
            },

            // Pinned columns (left-pinned "name" col)
            '& .MuiDataGrid-pinnedColumns, & .MuiDataGrid-pinnedColumnHeaders': {
              backgroundColor: `${colors.surface} !important`,
              boxShadow: 'none',
              borderRight: `1px solid ${colors.hairline}`,
            },

            // Filler rows + scroller + virtualizer + footer
            '& .MuiDataGrid-filler':           { backgroundColor: 'transparent' },
            '& .MuiDataGrid-scrollbarFiller':  { backgroundColor: `${colors.surface} !important` },
            '& .MuiDataGrid-virtualScroller':  { backgroundColor: 'transparent' },
            '& .MuiDataGrid-footerContainer':  { backgroundColor: colors.surface, borderTop: `1px solid ${colors.hairline}` },
            '& .MuiDataGrid-overlay':          { backgroundColor: 'transparent' },
            '& .MuiTablePagination-root':      { color: 'text.secondary' },

            // Skeleton loading cells
            '& .MuiSkeleton-root': { bgcolor: colors.skeleton },

            // Custom cell classes
            '& .tnum':      { fontVariantNumeric: 'tabular-nums' },
            '& .tnum-bold': { fontVariantNumeric: 'tabular-nums', fontWeight: 600 },
          }}
        />
        </Card>
      </Box>
      )}

      {/* Edit-benchmarks dialog moved to the dedicated Benchmarks page. */}
    </Box>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// Asset-owner P0 widgets — underperformer leaderboard + forward calendar.
// Both live near the top of the page (above the existing alerts row) because
// they answer the two questions an asset manager asks when opening the page:
//   1. Where are my problems? → Underperformer leaderboard
//   2. What's coming up?      → Forward calendar
// ════════════════════════════════════════════════════════════════════════════


// ── UnderperformerLeaderboard ───────────────────────────────────────────────
// Ranks portfolio assets by capacity factor and surfaces the bottom 3 with
// a context-aware delta-to-portfolio-median chip. Click-through wires into
// Commercial Performance for that asset (the most likely "go look" target
// for an underperforming asset).
//
// Why CF (not revenue) for the ranking: revenue is a function of both
// generation AND price, and an asset that captures a great price on a low
// volume isn't necessarily under-performing operationally. CF normalises
// for capacity and surfaces the "this asset's not converting resource"
// signal that's actionable for the asset manager.
function UnderperformerLeaderboard({ ranked, loading, colors }) {
  const navigate = useNavigate();
  const { portfolioId } = useParams();

  // `ranked` is server-computed (see /overview kpis.bottom_n_underperformers
  // + kpis.median_cf_pct). No client-side derivation any more — keeps the
  // agent and the UI in lock-step.
  ranked = ranked || { bottom: [], median: null, total: 0 };

  return (
    <Card sx={{ p: 2.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Needs attention
        </Typography>
        {ranked.median != null && (
          <Typography variant="caption" sx={{
            color: 'text.disabled', fontVariantNumeric: 'tabular-nums',
          }}>
            Portfolio median CF: {ranked.median.toFixed(1)}%
          </Typography>
        )}
      </Stack>
      {loading ? (
        <Stack spacing={1.25}>
          {[0, 1, 2].map((i) => <Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius: 1 }} />)}
        </Stack>
      ) : !ranked.bottom.length ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.disabled" sx={{ fontSize: '0.85rem' }}>
            No assets with valid CF this period.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.25}>
          {ranked.bottom.map((a, i) => {
            const cf = a.cf_pct || 0;
            const delta = ranked.median != null ? cf - ranked.median : null;
            const deltaSign = delta != null && delta >= 0 ? '+' : '−';
            const deltaAbs  = delta != null ? Math.abs(delta).toFixed(1) : '—';
            const deltaColor = delta != null
              ? (delta < -5 ? '#ef4444' : delta < 0 ? '#f59e0b' : '#22c55e')
              : 'text.disabled';
            return (
              <Box
                key={a.asset_id}
                onClick={() => navigate(`/portfolios/${portfolioId}/commercial?asset_id=${a.asset_id}`)}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr auto auto' },
                  columnGap: 2, rowGap: 0.5,
                  alignItems: 'center',
                  p: 1.5, borderRadius: 1.5,
                  cursor: 'pointer',
                  border: '1px solid', borderColor: 'divider',
                  bgcolor: 'rgba(239, 68, 68, 0.04)',
                  transition: 'all 120ms ease',
                  '&:hover': {
                    bgcolor: 'rgba(239, 68, 68, 0.10)',
                    borderColor: '#ef4444',
                    transform: 'translateX(2px)',
                  },
                }}>
                {/* Rank badge */}
                <Box sx={{
                  width: 28, height: 28, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: '#ef44441f', color: '#ef4444',
                  fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace',
                }}>
                  #{i + 1}
                </Box>
                {/* Name + meta */}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: '0.92rem', fontWeight: 600, color: 'text.primary',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {a.name}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.72rem', color: 'text.disabled', mt: 0.1,
                  }}>
                    {a.region} · {a.fuel} · {Math.round(a.capacity_mw || 0)} MW
                  </Typography>
                </Box>
                {/* CF + delta */}
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{
                    fontSize: '1.05rem', fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    color: cf < 10 ? '#ef4444' : 'text.primary',
                    lineHeight: 1.1,
                  }}>
                    {cf.toFixed(1)}%
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.7rem', color: deltaColor, fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {delta != null ? `${deltaSign}${deltaAbs}pp vs median` : 'CF'}
                  </Typography>
                </Box>
                {/* Drill-in caret */}
                <Box sx={{ color: 'text.disabled', fontSize: '1.1rem' }}>›</Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </Card>
  );
}


// ── ForwardCalendarCard ─────────────────────────────────────────────────────
// 90-day forward outlook — surfaces events that need the asset manager's
// attention in the coming months. Stays out of the way of the operational
// pulse (which is what the rest of the page shows) by anchoring on FUTURE
// events only.
//
// Event types:
//   • PPA expiries          (real data — when contracts module ships)
//   • Scheduled maintenance (STUB — needs ops calendar feed)
//   • COD / commissioning   (STUB — needs pipeline data)
//   • Refinancing windows   (STUB — needs debt facility metadata)
//
// For first cut: deterministic stub events per asset so the layout reads
// like a real calendar. Each event is clearly marked as STUB until its
// real data source is wired.
function ForwardCalendarCard({ assets, loading, colors }) {
  const events = useMemo(() => {
    if (!assets?.length) return [];
    // Synthesise plausible upcoming events from the portfolio's asset list.
    // We rotate event types across the assets so the demo looks varied.
    // Each event has: { date, kind, asset_id, name, label, impact }
    const now = new Date();
    const out = [];
    assets.slice(0, 6).forEach((a, i) => {
      // Stagger events across the 90d window.
      const daysOut = 8 + i * 14;
      const date = new Date(now);
      date.setDate(date.getDate() + daysOut);
      const eventTypes = [
        { kind: 'maintenance', label: 'Scheduled inverter maintenance', impact: 'Est. 2 days outage', color: '#f59e0b' },
        { kind: 'ppa',         label: 'PPA renewal window opens',       impact: '14 months to expiry',  color: '#3b82f6' },
        { kind: 'maintenance', label: 'Annual inspection',              impact: 'Est. 1 day outage',  color: '#f59e0b' },
        { kind: 'refinancing', label: 'Debt facility review',           impact: 'AUD facility · floating', color: '#8b5cf6' },
        { kind: 'cod',         label: 'COD anniversary review',         impact: 'Performance audit',  color: '#22c55e' },
        { kind: 'ppa',         label: 'PPA strike review',              impact: 'Indexation clause',  color: '#3b82f6' },
      ];
      const ev = eventTypes[i % eventTypes.length];
      out.push({
        date,
        kind: ev.kind,
        asset_id: a.asset_id,
        name: a.name,
        region: a.region,
        label: ev.label,
        impact: ev.impact,
        color: ev.color,
      });
    });
    return out.sort((a, b) => a.date - b.date);
  }, [assets]);

  const fmt = (d) => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  const daysFromNow = (d) => Math.round((d - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <Card sx={{ p: 2.5, minHeight: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Forward calendar · next 90 days
        </Typography>
        <Typography variant="caption" sx={{
          color: '#f59e0b', fontWeight: 600, fontSize: '0.66rem',
          letterSpacing: '0.04em', textTransform: 'uppercase',
        }}>
          synthetic
        </Typography>
      </Stack>
      {loading ? (
        <Stack spacing={1}>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={40} sx={{ borderRadius: 1 }} />)}
        </Stack>
      ) : !events.length ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.disabled" sx={{ fontSize: '0.85rem' }}>
            No events in the forward window.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={0.8} sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
          {events.map((ev, i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={1.25} sx={{
              p: 1, borderRadius: 1,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
            }}>
              {/* Date pill */}
              <Box sx={{
                minWidth: 56, textAlign: 'center',
                px: 0.75, py: 0.5, borderRadius: 1,
                bgcolor: `${ev.color}1f`, color: ev.color,
              }}>
                <Typography sx={{
                  fontSize: '0.7rem', fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1.1,
                }}>
                  {fmt(ev.date)}
                </Typography>
                <Typography sx={{
                  fontSize: '0.6rem', opacity: 0.75,
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1,
                }}>
                  +{daysFromNow(ev.date)}d
                </Typography>
              </Box>
              {/* Description */}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontSize: '0.82rem', fontWeight: 500, color: 'text.primary',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {ev.label}
                </Typography>
                <Typography sx={{
                  fontSize: '0.7rem', color: 'text.disabled', mt: 0.1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {ev.name} · {ev.impact}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
    </Card>
  );
}


// ── RevenueMixCard ──────────────────────────────────────────────────────────
// "Where is my money coming from?" — donut of period revenue split by fuel,
// with a bottom strip showing portfolio-level ESG numbers (CO2 avoided,
// LGCs, homes powered) for LP / board reporting. Aggregates client-side
// from data.assets[].revenue_aud + .generation_mwh (which /overview now
// populates per-asset). Falls back to a soft empty state when no revenue
// is recorded for the period.
//
// Why this and not curtailment cause attribution: asset owners care about
// revenue concentration + impact story at the portfolio level. Cause-of-
// curtailment is operational and lives on Commercial / Technical pages
// where the period + asset filters are more granular.
function RevenueMixCard({ fuelTotals, esg, loading, colors }) {
  // `fuelTotals` (revenue_mix_by_fuel) and `esg` are server-computed.
  // Previously aggregated client-side via reduce/Map; now a single source
  // of truth in modules/portfolio/overview.py so the AI agent and UI agree.
  // Backend returns generation_mwh; this component historically used `mwh`.
  fuelTotals = (fuelTotals || [])
    .filter((r) => (r.revenue_aud || 0) > 0)
    .map((r) => ({
      fuel: r.fuel,
      revenue_aud: r.revenue_aud || 0,
      mwh: r.generation_mwh || 0,
      share_pct: r.share_pct,
    }));
  const totalRev = fuelTotals.reduce((a, b) => a + b.revenue_aud, 0);

  const co2_t  = esg?.co2_displaced_t  ?? 0;
  const lgcs   = esg?.lgcs_eligible    ?? 0;
  const homes  = esg?.homes_equivalent ?? 0;

  // Donut data — uses the same FUEL_COLOR palette as the rest of the page.
  const slices = fuelTotals.map((r) => ({
    id: r.fuel.toLowerCase(),
    label: r.fuel,
    value: r.revenue_aud,
    color: FUEL_COLOR[r.fuel] || FUEL_COLOR.Other,
  }));

  return (
    <Card sx={{ p: 2.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Revenue mix
        </Typography>
        <Typography variant="caption" sx={{
          color: 'text.disabled', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
        }}>
          {fmtAUDCompact(totalRev)} total
        </Typography>
      </Stack>
      {loading ? (
        <Skeleton variant="rectangular" height={220} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
      ) : !slices.length ? (
        <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.disabled">No revenue for the selected window.</Typography>
        </Box>
      ) : (
        <Box>
          <Box sx={{
            display: 'grid',
            // Reserve a fixed donut column so the legend can't be crushed
            // on narrow viewports. Same pattern as Commercial Performance.
            gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' },
            alignItems: 'center',
            columnGap: 2.5, rowGap: 2,
            mb: 2,
          }}>
            {/* Donut */}
            <Box sx={{ position: 'relative', width: 160, height: 160, mx: 'auto' }}>
              <PieChart
                width={160}
                height={160}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                hideLegend
                series={[{
                  data: slices,
                  innerRadius: 52,
                  outerRadius: 76,
                  paddingAngle: 1.5,
                  cornerRadius: 2,
                  highlightScope: { faded: 'global', highlighted: 'item' },
                  faded: { innerRadius: 52, additionalRadius: -2, color: 'gray' },
                  valueFormatter: (item) => {
                    const pct = totalRev > 0 ? (item.value / totalRev) * 100 : 0;
                    return `${fmtAUDCompact(item.value)} · ${pct.toFixed(1)}%`;
                  },
                }]}
              />
              {/* Centre overlay — total revenue */}
              <Box sx={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'none',
              }}>
                <Typography sx={{
                  fontSize: '1.1rem', fontWeight: 700, lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums', color: 'text.primary',
                  whiteSpace: 'nowrap',
                }}>
                  {fmtAUDCompact(totalRev)}
                </Typography>
                <Typography sx={{
                  fontSize: '0.6rem', color: 'text.disabled',
                  letterSpacing: '0.1em', textTransform: 'uppercase',
                  mt: 0.5, whiteSpace: 'nowrap',
                }}>
                  Total revenue
                </Typography>
              </Box>
            </Box>

            {/* Legend — fuel · revenue · share */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr auto auto',
              columnGap: 1.25, rowGap: 0.8,
              alignItems: 'center',
            }}>
              {slices.map((s) => {
                const r = fuelTotals.find((f) => f.fuel.toLowerCase() === s.id);
                const pct = totalRev > 0 ? (s.value / totalRev) * 100 : 0;
                return (
                  <React.Fragment key={s.id}>
                    <Box sx={{
                      width: 9, height: 9, borderRadius: '50%', bgcolor: s.color,
                    }} />
                    <Typography sx={{
                      fontSize: '0.82rem', fontWeight: 500, color: 'text.primary',
                    }}>
                      {s.label}
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.82rem', fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums', textAlign: 'right',
                    }}>
                      {fmtAUDCompact(s.value)}
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.72rem', color: 'text.disabled',
                      fontVariantNumeric: 'tabular-nums', textAlign: 'right',
                    }}>
                      {pct.toFixed(1)}%
                    </Typography>
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>

          {/* ESG strip — quick LP-friendly numbers. The factors are AEMO /
              AER published constants; once a region-specific emissions
              feed lands we can refine. Until then this gives a sensible
              order-of-magnitude that's useful for board-pack copy. */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1.25,
            pt: 2,
            borderTop: '1px solid', borderColor: 'divider',
          }}>
            {[
              { label: 'CO₂ avoided',  value: `${(co2_t / 1000).toFixed(1)} kt`,    sub: '× 0.71 t/MWh (NEM avg)' },
              { label: 'LGCs',         value: lgcs.toLocaleString(),                sub: '1 per MWh' },
              { label: 'Homes powered',value: `${Math.round(homes).toLocaleString()}`, sub: '@ 6 MWh/yr' },
            ].map((m) => (
              <Box key={m.label}>
                <Typography sx={{
                  fontSize: '0.62rem', color: 'text.disabled',
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                }}>
                  {m.label}
                </Typography>
                <Typography sx={{
                  fontSize: '1rem', fontWeight: 700, lineHeight: 1.1,
                  fontVariantNumeric: 'tabular-nums', color: '#22c55e',
                  mt: 0.25,
                }}>
                  {m.value}
                </Typography>
                <Typography sx={{
                  fontSize: '0.65rem', color: 'text.disabled', mt: 0.15,
                }}>
                  {m.sub}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Card>
  );
}
